import type { Rule } from "antd/lib/form";

/*
 * XML 1.0 forbids a fixed set of characters outright: they cannot appear in a
 * document at all, escaped or not. A value carrying one of them reaches ELSTER
 * as malformed XML and comes back as ERiC 610301006, a parse error naming
 * neither the field nor the character. Diagnosing one cost two wrong root
 * causes on SMT-1262 before anyone looked at the text itself.
 *
 * The set is the complement of the XML 1.0 `Char` production
 *   Char ::= #x9 | #xA | #xD | [#x20-#xD7FF] | [#xE000-#xFFFD] | [#x10000-#x10FFFF]
 * so: control characters other than tab, line feed and carriage return, the
 * non-characters U+FFFE and U+FFFF, and surrogate code units that are not part
 * of a valid pair. Everything a German business name legitimately uses --
 * umlauts, ampersands, apostrophes, hyphens, slashes -- is allowed and stays
 * untouched.
 *
 * The rule comes from the specification rather than from the character that
 * caused the original failure: that value was cleaned during diagnosis before
 * anyone recorded it, so the character itself is unknown. Anything breaking
 * well-formedness is in this set by definition.
 *
 * The same rule lives in kontax-backend (utilities/xmlSafeText.ts) and in
 * kontax-neric-api (routes/v1/fse/shared.js). Three repositories, no shared
 * package: if one changes, change the others. This copy is the one that
 * reaches the operator while they still have the field in front of them.
 *
 * See SMT-1295.
 */
export const findXmlForbiddenCharacter = (
  value: string
): { index: number; code: number } | undefined => {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code <= 0x1f && code !== 0x09 && code !== 0x0a && code !== 0x0d) {
      return { index, code };
    }

    if (code === 0xfffe || code === 0xffff) {
      return { index, code };
    }

    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) {
        return { index, code };
      }
      // A valid pair is one character; skip its low half.
      index += 1;
      continue;
    }

    if (code >= 0xdc00 && code <= 0xdfff) {
      return { index, code };
    }
  }

  return undefined;
};

export const isXmlSafeText = (value: string): boolean =>
  findXmlForbiddenCharacter(value) === undefined;

export const formatCodePoint = (code: number): string =>
  `U+${code.toString(16).toUpperCase().padStart(4, "0")}`;

/*
 * Takes a formatter rather than a finished string so the message can name both
 * the field and the code point, which is what makes the error actionable: the
 * offending character is invisible in the input by definition.
 */
export const xmlSafeTextRule = (
  formatMessage: (codePoint: string) => string
): Rule => ({
  validator: (_, value?: string) => {
    const found =
      typeof value === "string" ? findXmlForbiddenCharacter(value) : undefined;

    return found
      ? Promise.reject(new Error(formatMessage(formatCodePoint(found.code))))
      : Promise.resolve();
  },
});

/*
 * `min={0}` on the InputNumber only clamps on blur, so a pasted or
 * programmatically written negative reaches the form value untouched. This
 * validates the parsed value instead, and leaves an empty field to the
 * required rule rather than reporting it as negative.
 */
export const nonNegativeAmountRule = (message: string): Rule => ({
  validator: (_, value?: number | string | null) => {
    if (value === undefined || value === null || value === "") {
      return Promise.resolve();
    }

    const numeric = typeof value === "number" ? value : Number(value);

    return Number.isFinite(numeric) && numeric < 0
      ? Promise.reject(new Error(message))
      : Promise.resolve();
  },
});
